// 导入 React 相关 hooks 和类型
import { useCallback, useEffect, useRef, useState } from 'react';

// 导入组件 props 类型定义
import type { CollapseProps } from './type';
// 导入样式文件
import './index.scss';
import { useUpdate } from 'ahooks';

// 定义组件状态常量
const COLLAPSED = 'collapsed';    // 已折叠状态
const COLLAPSING = 'collapsing';  // 正在折叠中状态
const EXPANDING = 'expanding';    // 正在展开中状态
const EXPANDED = 'expanded';      // 已展开状态

// 定义默认值常量
const defaultClassName = 'collapse-css-transition';  // 默认 CSS 类名
const defaultElementType = 'div';                   // 默认渲染元素类型
const defaultCollapseHeight = '0px';                // 默认折叠高度

// 下一帧执行函数，用于动画处理
function nextFrame(callback: FrameRequestCallback) {
  requestAnimationFrame(() => {
    // 使用双重 requestAnimationFrame 确保在下一帧执行
    requestAnimationFrame(callback);
  });
}

// Collapse 组件定义
export const Collapse: React.FunctionComponent<CollapseProps> = ({
  children,         // 子元素
  transition,       // 过渡动画属性
  style,            // 自定义样式
  render,           // 渲染函数
  elementType = defaultElementType, // 元素类型，默认为 div
  isOpen,           // 是否展开
  collapseHeight = defaultCollapseHeight, // 折叠高度，默认为 0px
  onInit,           // 初始化回调
  onChange,         // 状态变化回调
  className = defaultClassName, // CSS 类名
  addState,         // 是否添加状态类名
  noAnim,           // 是否禁用动画
  overflowOnExpanded, // 展开时是否允许溢出
  ...rest           // 其他 props
}: CollapseProps): React.JSX.Element => {
  // 获取折叠状态的 visibility 值
  const getCollapsedVisibility = () =>
    collapseHeight === '0px' ? 'hidden' : undefined;

  // 强制更新组件的 reducer
  const forceUpdate = useUpdate();

  // 元素引用
  const elementRef = useRef();
  // 回调触发标记
  const [callbackTick, setCallbackTick] = useState(0);

  // 组件状态管理，使用 ref 避免不必要的重新渲染
  const state = useRef({
    collapse: isOpen ? EXPANDED : COLLAPSED, // 当前折叠状态
    style: { // 当前样式
      height: isOpen ? undefined : collapseHeight,
      visibility: isOpen ? undefined : getCollapsedVisibility(),
    },
  }).current;

  // 监听 callbackTick 变化，触发 onChange 回调
  useEffect(() => {
    callbackTick && onCallback(onChange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callbackTick]);

  // 通用回调函数
  const onCallback = (callback, params = {}) => {
    if (callback) {
      callback({ state: state.collapse, style: state.style, ...params });
    }
  };

  // 设置为折叠状态
  function setCollapsed() {
    if (!elementRef.current) return; // 安全检查

    // 更新状态为已折叠
    state.collapse = COLLAPSED;
    // 更新样式
    state.style = {
      height: collapseHeight,
      visibility: getCollapsedVisibility(),
    };
    forceUpdate(); // 强制更新组件

    // 异步触发回调
    const t = setTimeout(() => {
      setCallbackTick(Date.now);
      clearTimeout(t);
    }, 0);
  }

  // 设置为正在折叠状态
  function setCollapsing() {
    if (!elementRef.current) return;

    // 如果禁用动画，直接设置为折叠状态
    if (noAnim) {
      return setCollapsed();
    }

    // 更新状态为正在折叠
    state.collapse = COLLAPSING;
    // 设置当前高度为元素完整高度
    state.style = {
      height: getElementHeight(),
      visibility: undefined,
    };
    forceUpdate();

    // 下一帧开始折叠动画
    nextFrame(() => {
      if (!elementRef.current) return;
      if (state.collapse !== COLLAPSING) return;

      // 设置目标高度为折叠高度
      state.style = {
        height: collapseHeight,
        visibility: undefined,
      };

      setCallbackTick(Date.now); // 触发回调
    });
  }

  // 设置为正在展开状态
  function setExpanding() {
    if (!elementRef.current) return;

    // 如果禁用动画，直接设置为展开状态
    if (noAnim) {
      return setExpanded();
    }

    // 更新状态为正在展开
    state.collapse = EXPANDING;

    // 下一帧开始展开动画
    nextFrame(() => {
      if (!elementRef.current) return;
      if (state.collapse !== EXPANDING) return;

      // 设置目标高度为元素完整高度
      state.style = {
        height: getElementHeight(),
        visibility: undefined,
      };

      setCallbackTick(Date.now); // 触发回调
    });
  }

  // 设置为展开状态
  function setExpanded() {
    if (!elementRef.current) return;

    // 更新状态为已展开
    state.collapse = EXPANDED;
    // 清除高度限制
    state.style = {
      height: undefined,
      visibility: undefined,
    };
    forceUpdate();

    // 异步触发回调
    setTimeout(() => setCallbackTick(Date.now), 0);
  }

  // 获取元素完整高度
  function getElementHeight() {
    // @ts-ignore
    return `${elementRef.current.scrollHeight}px`;
  }

  // 过渡动画结束事件处理
  function onTransitionEnd({ target, propertyName }) {
    // 确保是高度属性的过渡结束
    if (target === elementRef.current && propertyName === 'height') {
      const styleHeight = target.style.height;

      switch (state.collapse) {
        case EXPANDING: // 展开动画结束
          if (styleHeight === undefined || styleHeight === collapseHeight)
            console.warn(
              `onTransitionEnd height unexpected ${styleHeight}`,
              'ignore setExpanded',
            );
          else setExpanded(); // 最终设置为展开状态
          break;
        case COLLAPSING: // 折叠动画结束
          if (styleHeight === undefined || styleHeight !== collapseHeight)
            console.warn(
              `onTransitionEnd height unexpected ${styleHeight}`,
              'ignore setCollapsed',
            );
          else setCollapsed(); // 最终设置为折叠状态
          break;
        default:
          console.warn('Ignored in onTransitionEnd', state.collapse);
      }
    }
  }

  // 类似于 getDerivedStateFromProps 的逻辑
  const didOpen = state.collapse === EXPANDED || state.collapse === EXPANDING;

  // 根据 props.isOpen 的变化更新状态
  if (!didOpen && isOpen) setExpanding(); // 从折叠到展开
  if (didOpen && !isOpen) setCollapsing(); // 从展开到折叠

  // 处理溢出属性
  const overflow =
    state.collapse === EXPANDED && overflowOnExpanded ? undefined : 'hidden';

  // 计算最终样式
  const computedStyle = {
    overflow,
    transition,
    ...style,
    ...state.style,
  };
  const ElementType = elementType;

  // 元素引用回调
  const callbackRef = useCallback(
    (node) => {
      if (node) {
        elementRef.current = node;
        onCallback(onInit, { node }); // 触发初始化回调
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [elementType],
  );

  // 计算最终类名
  const collapseClassName = addState
    ? `${className} --c-${state.collapse}`
    : className;

  // 渲染组件
  return (
    // @ts-ignore
    // eslint-disable-next-line react/react-in-jsx-scope
    <ElementType
      ref={callbackRef}
      style={computedStyle}
      onTransitionEnd={onTransitionEnd}
      className={collapseClassName}
      {...rest}
    >
      {(typeof children === 'function' && children(state.collapse)) ||
       (typeof render === 'function' && render(state.collapse)) || 
       children}
    </ElementType>
  );
};
